import { useState, useEffect } from "react";
import {
  Box,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Select,
} from "@mui/material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Wrapper} from "../../components";
import { conpanyData, conpanyUsersData, startImpersonation} from "../../services";
import SearchIcon from "@mui/icons-material/Search";
import { UserManagementTable } from "../../components/table/UserManagementTable";

dayjs.extend(relativeTime);

export function UserImpersonation() {
  const { user } = useSelector((state) => state.auth);
  const jwtToken = user?.jwt;
  const CONTROL_HEIGHT = 38;

  const [companyId, setCompanyId] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [dropDownData, setDropDownData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [totalCustomersCount, setTotalCustomersCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);

    /* ================= API CALLS ================= */
  useEffect(() => {
      if (!jwtToken) return;
      const fetchCompanyData = async () => {
      try {
      const companyRes = await conpanyData(jwtToken);
      if (companyRes?.status === 200) {
        setDropDownData(companyRes.data?.data || []);
      }
      } catch (error) {
        console.error("Dashboard API error:", error);
      } 
    };
    fetchCompanyData();
  }, [jwtToken]);
  
  useEffect(() => {
    if (!jwtToken) return;
  
    const fetchUsers = async () => {
      try {
        setTableLoading(true);
        const selectedCompanyId = companyId === "all" ? undefined : companyId;
        const res = await conpanyUsersData(jwtToken, page, rowsPerPage, selectedCompanyId, search);
        if (res?.status === 200) {
          setCustomers(res.data?.data || []);
          setTotalCustomersCount(
            res.data?.pagination?.total || 0
          );
        }
      } catch (error) {
        console.error("Top customers API error:", error);
      } finally {
        setTableLoading(false);
      }
    };
    fetchUsers();
  }, [jwtToken, companyId, search, page, rowsPerPage]);
  
  useEffect(() => {
    setPage(1);
  }, [companyId, search, rowsPerPage]);

  const handleImpersonate = async (row) => {
    try {
      const res = await startImpersonation(jwtToken, row.id);
      if (res?.status === 200 && res.data?.loginUrl) {
        const sourceURL = `${window.location.origin}/company-users`;
        window.location.href = `${res.data.loginUrl}&source=${sourceURL}`;
      }
    } catch (error) {
      console.error("Impersonation failed:", error);
    }
  };
  
  /* ================= UI ================= */
  return (
    <Wrapper>
      <Stack sx={{ pt: 2, mt: 7 }} spacing={2} className="wrapper">
        <Typography color="black" fontWeight="regular" sx={{ opacity: 0.8 }}>
          View and manage users across all registered companies.
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          {/* Search */}
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Users..."
            size="small"
            sx={{
              width: 280,
              "& .MuiOutlinedInput-root": {
                height: CONTROL_HEIGHT,
                borderRadius: 3,
                backgroundColor: "#fff",

                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },

                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px #fff inset",
                  WebkitTextFillColor: "#000",
                },
              },

              "& input::selection": {
                backgroundColor: "#e2e8f0",
                color: "#000",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Status Filter */}
          <Select
            size="small"
            value={companyId}
            onChange={(e) => {
              setCompanyId(e.target.value);
              setPage(1);
            }}
            sx={{
              ml: "auto",
              height: CONTROL_HEIGHT,
              minWidth: 220,
              borderRadius: 3,
              backgroundColor: "#fff",
              "& .MuiSelect-select": {
                fontSize: "14px",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
              },
                "@media (max-width: 576px)": {
                  ml: "0px",
                }
            }}
          >
            {/* Default */}
            <MenuItem value="all">All Companies</MenuItem>

            {/* API-driven */}
            {dropDownData.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ mt: 3 }}>
        <UserManagementTable
            tableLoading={tableLoading}
            data={customers}
            totalCount={totalCustomersCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            onImpersonate={handleImpersonate}
          />
       </Box>
       <Box sx={{ mt: 1 }}> </Box>
      </Stack>
    </Wrapper>
  );
} 